import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { LucideIcon } from "lucide-react";
import { ChevronDown, LogOut } from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { cn } from "@/lib/utils";
import { Link, useLocation } from "react-router-dom";
import { useLogout } from "@/hooks/useLogout";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

export interface PanelNavChild {
  title: string;
  url: string;
}

export interface PanelNavItem {
  title: string;
  url: string;
  icon: LucideIcon;
  badge?: number;
}

export type PanelNavLeaf = PanelNavItem & { type?: "leaf" };

export type PanelNavGroup = {
  type: "group";
  title: string;
  icon: LucideIcon;
  children: PanelNavChild[];
};

export type PanelNavEntry = PanelNavLeaf | PanelNavGroup;

export interface PanelBrand {
  icon: LucideIcon;
  iconClassName?: string;
  iconIconClassName?: string;
  title: string;
  subtitle?: string;
  url: string;
}

export interface PanelSidebarProps {
  collapsed: boolean;
  brand: PanelBrand;
  navItems: PanelNavEntry[];
  footer: { label: string; url: string };
  sectionLabel?: string;
}

function isNavGroup(entry: PanelNavEntry): entry is PanelNavGroup {
  return entry.type === "group";
}

function isActiveRoute(pathname: string, url: string) {
  const segments = url.split("/").filter(Boolean);
  if (segments.length <= 1) return pathname === url;
  return pathname === url || pathname.startsWith(`${url}/`);
}

function isGroupActive(pathname: string, children: PanelNavChild[]) {
  return children.some((child) => isActiveRoute(pathname, child.url));
}

const PanelSidebar = ({ collapsed, brand, navItems, footer, sectionLabel = "MENU" }: PanelSidebarProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const BrandIcon = brand.icon;
  const logout = useLogout();
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  const translateTitle = (title: string) => {
    switch (title) {
      case "Overview": return t("panel.overview");
      case "Profile": return t("panel.profile");
      case "Personal Info": return t("panel.personal_info");
      case "Member ID": return t("panel.member_id");
      case "KYC Verification": return t("panel.kyc");
      case "My Orders": return t("panel.my_orders");
      case "Wallet": return t("panel.wallet");
      case "Balance & P2P": return t("panel.wallet_balance");
      case "Transactions": return t("panel.transactions");
      case "Rewards & BV": return t("panel.rewards");
      case "Referrals": return t("panel.referrals");
      case "My Team": return t("panel.my_team");
      case "Direct Referral": return t("panel.direct_referral");
      case "Level List": return t("panel.level_list");
      case "Income Reports": return t("panel.income_reports");
      case "Self Cashback": return t("panel.self_cashback");
      case "Level Income": return t("panel.level_income");
      case "Reward Income": return t("panel.reward_income");
      case "Addresses": return t("panel.addresses");
      case "Notifications": return t("panel.notifications");
      case "Products": return t("panel.products");
      case "Orders": return t("panel.orders");
      case "Earnings": return t("panel.earnings");
      case "Store KYC": return t("panel.store_kyc");
      case "Dashboard": return t("panel.dashboard");
      case "My Deliveries": return t("panel.my_deliveries");
      default: return title;
    }
  };

  const translateSectionLabel = (label: string) => {
    switch (label) {
      case "MENU": return t("nav.section_main");
      case "ACCOUNT": return t("nav.section_manage");
      case "SELLER PANEL": return t("nav.section_manage");
      default: return label;
    }
  };

  const translatedFooterLabel = footer.label === "Sign Out" || footer.label === "Log Out" ? t("logout") : footer.label;

  useEffect(() => {
    navItems.forEach((entry) => {
      if (isNavGroup(entry) && isGroupActive(location.pathname, entry.children)) {
        setOpenGroups((prev) => ({ ...prev, [entry.title]: true }));
      }
    });
  }, [location.pathname, navItems]);

  const renderLeafLink = (
    item: { title: string; url: string; icon?: LucideIcon; badge?: number },
    options?: { nested?: boolean },
  ) => {
    const Icon = item.icon;
    const nested = options?.nested ?? false;
    const translatedTitle = translateTitle(item.title);

    const content = (
      <NavLink
        to={item.url}
        end={nested || item.url.split("/").filter(Boolean).length <= 1}
        className={cn(
          "group relative flex items-center gap-3 rounded-lg py-2 text-[13px] font-medium transition-all duration-200",
          "text-neutral-400 hover:text-white hover:bg-white/10",
          nested ? "pl-9 pr-3" : "px-3",
          !nested && collapsed && "mx-auto h-10 w-10 justify-center px-0",
        )}
        activeClassName={cn(
          "bg-gradient-primary text-primary-foreground shadow-md hover:text-primary-foreground",
          !nested && collapsed && "shadow-glow",
        )}
      >
        {Icon ? (
          <Icon className={cn("h-[18px] w-[18px] shrink-0", !nested && collapsed && "h-5 w-5")} />
        ) : (
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-600 group-hover:bg-neutral-400" />
        )}
        {(!collapsed || nested) && (
          <>
            <span className="flex-1 truncate">{translatedTitle}</span>
            {item.badge ? (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-bold text-destructive-foreground">
                {item.badge}
              </span>
            ) : null}
          </>
        )}
        {!nested && collapsed && item.badge ? (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[9px] font-bold text-destructive-foreground ring-2 ring-black">
            {item.badge}
          </span>
        ) : null}
      </NavLink>
    );

    if (collapsed && !nested) {
      return (
        <Tooltip key={item.title} delayDuration={0}>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent
            side="right"
            sideOffset={8}
            className="z-[60] rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-1.5 text-xs font-medium text-white shadow-xl"
          >
            {translatedTitle}
          </TooltipContent>
        </Tooltip>
      );
    }

    return <div key={item.title}>{content}</div>;
  };

  const renderNavEntry = (entry: PanelNavEntry) => {
    if (!isNavGroup(entry)) {
      return renderLeafLink(entry);
    }

    const groupActive = isGroupActive(location.pathname, entry.children);
    const isOpen = openGroups[entry.title] ?? groupActive;

    if (collapsed) {
      return (
        <div key={entry.title} className="space-y-0.5">
          {entry.children.map((child) =>
            renderLeafLink({ title: child.title, url: child.url, icon: entry.icon }),
          )}
        </div>
      );
    }

    return (
      <Collapsible
        key={entry.title}
        open={isOpen}
        onOpenChange={(open) => setOpenGroups((prev) => ({ ...prev, [entry.title]: open }))}
        className="space-y-0.5"
      >
        <CollapsibleTrigger
          className={cn(
            "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-[13px] font-medium transition-all duration-200",
            "text-neutral-400 hover:bg-white/10 hover:text-white",
            groupActive && "text-white",
          )}
        >
          <entry.icon className="h-[18px] w-[18px] shrink-0" />
          <span className="flex-1 truncate text-left">{translateTitle(entry.title)}</span>
          <ChevronDown
            className={cn(
              "h-4 w-4 shrink-0 transition-transform duration-300 ease-in-out",
              isOpen && "rotate-180",
            )}
          />
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-0.5 pt-0.5">
          {entry.children.map((child) => renderLeafLink({ title: child.title, url: child.url }, { nested: true }))}
        </CollapsibleContent>
      </Collapsible>
    );
  };

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 z-30 glass-sidebar flex h-screen flex-col transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] max-lg:hidden",
        collapsed ? "w-[68px]" : "w-[216px]",
      )}
    >
      <div
        className={cn(
          "flex h-16 shrink-0 items-center border-b border-white/10",
          collapsed ? "justify-center px-2" : "px-4",
        )}
      >
        <Link
          to={brand.url}
          className={cn("flex items-center gap-2.5", collapsed ? "justify-center" : "min-w-0 flex-1")}
        >
          {collapsed ? (
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white p-1.5 shadow-sm">
              <img src="/icon.png" alt="Icon" className="h-full w-full object-contain" />
            </div>
          ) : (
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white p-1.5 shadow-sm">
                <img src="/icon.png" alt="Icon" className="h-full w-full object-contain" />
              </div>
              <div className="flex flex-col">
                <div className="rounded-lg bg-white px-2 py-0.5 flex items-center justify-center h-7">
                  <img src="/nav-logo.png" alt="Bihari Mall" className="h-5 object-contain" />
                </div>
                {brand.subtitle && (
                  <span className="block text-[9px] font-bold uppercase tracking-wider text-neutral-500 mt-1 leading-none pl-1">
                    {brand.subtitle}
                  </span>
                )}
              </div>
            </div>
          )}
        </Link>
      </div>

      <nav className="scrollbar-hover flex-1 space-y-1 overflow-y-auto overflow-x-hidden px-2 py-3">
        <div className="space-y-0.5">
          {!collapsed ? (
            <p className="px-3 pb-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-neutral-500">
              {translateSectionLabel(sectionLabel)}
            </p>
          ) : (
            <div className="mx-auto my-1 w-6 border-t border-white/10" />
          )}
          {navItems.map(renderNavEntry)}
        </div>
      </nav>

      <div className="shrink-0 border-t border-white/10 p-2.5">
        {collapsed ? (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => logout()}
                className="mx-auto flex h-10 w-10 items-center justify-center rounded-lg text-neutral-400 transition-all hover:bg-red-500/10 hover:text-red-400"
              >
                <LogOut className="h-[18px] w-[18px]" />
              </button>
            </TooltipTrigger>
            <TooltipContent
              side="right"
              sideOffset={8}
              className="z-[60] rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-1.5 text-xs font-medium text-white shadow-xl"
            >
              {translatedFooterLabel}
            </TooltipContent>
          </Tooltip>
        ) : (
          <button
            type="button"
            onClick={() => logout()}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-[13px] font-medium text-neutral-400 transition-all hover:bg-red-500/10 hover:text-red-400"
          >
            <LogOut className="h-[18px] w-[18px] shrink-0" />
            <span>{translatedFooterLabel}</span>
          </button>
        )}
      </div>
    </aside>
  );
};

export default PanelSidebar;
